import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, withLatestFrom } from 'rxjs';
import { DatepickerRange, KendoSortDescriptor, RangeDatepickerComponent } from '@ui/aos-lib';

import { RmdEventsActions } from './rmd-events.actions';
import { selectFiltersPayload, selectTableState, selectDeviceUID } from './rmd-events.reducer';
import { CustomersApiService } from '../../../../../../csr/customers/api/customers-api.service';
import { SortMapping } from '../../../../../../csr/customers/services/customer-details.service';
import { CustomerFieldName } from '../../../../../../csr/customers/customers.constants';
import { Events } from '../../../../../../network-monitoring/events-monitor/events-monitor.types';
import { DurationPipe } from 'src/app/shared/pipes/duration.pipe';
import { OrderDirection } from '../../../../../../equipment/devices/components/devices.models';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { multiplierWithUom } from '../../../../../../../shared/utils';
import { EventsFieldName } from 'src/app/pages/csr/customers/components/customer-details-events/customer-details-events.constants';

const durationPipe = new DurationPipe();

function makeSortFilter(sort: KendoSortDescriptor[]): {
  columnName: CustomerFieldName;
  orderDirection: OrderDirection;
} {
  return {
    columnName: sort[0].field as CustomerFieldName,
    orderDirection: sort[0].dir ? (sort[0].dir as string as OrderDirection) : OrderDirection.DESC
  };
}

export const getEvents$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    customersApiService = inject(CustomersApiService),
    timezoneService = inject(TimezoneService)
  ) =>
    actions$.pipe(
      ofType(RmdEventsActions.stateChange),
      withLatestFrom(store.select(selectFiltersPayload), store.select(selectTableState), store.select(selectDeviceUID)),
      exhaustMap(([, filters, tableState, deviceUID]) => {
        const sort = tableState.sort && makeSortFilter(tableState.sort);
        const lastYearRange = {
          start: new Date((RangeDatepickerComponent.LAST_YEAR.getValue() as DatepickerRange).start),
          end: new Date((RangeDatepickerComponent.LAST_YEAR.getValue() as DatepickerRange).end)
        };
        const updatedFilters = [
          ...filters,
          {
            filterType: EventsFieldName.LastDetectedUtc,
            filteredValues: timezoneService.getShiftedDatepickerRangeStringified(lastYearRange)
          },
          {
            filterType: EventsFieldName.DeviceUID,
            filteredValues: [deviceUID!]
          }
        ];
        const payload = {
          ...tableState,
          filters: updatedFilters,
          sort: sort?.columnName
            ? {
                ...sort,
                columnName: SortMapping[
                  sort.columnName as unknown as keyof typeof SortMapping
                ] as unknown as EventsFieldName
              }
            : sort
        };

        return customersApiService.getDetailsEvents(payload).pipe(
          map(events => {
            const { items, totalCount } = events;
            const data: Events[] = items.map(event => {
              return {
                ...event,
                lastDetectedUtc: timezoneService.formatLong(event.lastDetectedUtc),
                startedAtUtc: timezoneService.formatLong(event.startedAtUtc),
                endedAtUtc: event.endedAtUtc ? timezoneService.formatLong(event.endedAtUtc) : null,
                duration: durationPipe.transform(event.lastDetectedUtc, event.endedAtUtc),
                statusText: event.status,
                unit: multiplierWithUom(event.multiplier, event.unitOfMeasure)
              };
            });
            return RmdEventsActions.getEventsSuccess({ eventsData: { data, total: totalCount } });
          }),
          catchError((error: unknown) => of(RmdEventsActions.getEventsFailure({ error: String(error) })))
        );
      })
    ),
  { functional: true }
);
